import { Capability, CapabilityNameMap, UserCapabilities } from '@vimeo/core/hooks/use-capability';
import { Definable, Maybe, Optional } from '@vimeo/core/types/generic';
import { GetUserCapabilitiesResult } from '@vimeo/gctl-api/fetchers/user/capabilities';
import { BadgeType, User } from '@vimeo/viewer';

import { decamelize } from './transform';

/**
 * @param user - optional user entity to check
 * @returns if user is staff account
 */
export function isStaffUser(user: Maybe<User>): boolean {
  return user?.badge?.type === BadgeType.Staff;
}

/**
 * @param user - target user to get avatar for
 * @param config - configuration of avatar to get
 * @returns possible avatar of maximal allowed width
 */
export function getUserAvatar(
  user: Maybe<User>,
  config: { maximalAllowedWidth: number } = { maximalAllowedWidth: 360 },
): Optional<{ width: number; height: number; link: string }> {
  return (
    user?.pictures?.sizes
      ?.filter((it) => it.width <= config.maximalAllowedWidth)
      .sort((first, second) => second.width - first.width)[0] || null
  );
}

/**
 * Normalize list of raw capabilities to a matching transformed has/can variables.
 *
 * @param rawCapabilities - response from API, raw capabilities
 * @param capabilityMap - naming map for capabilities to transform server values to front-end friendly values
 * @returns map of user capabilities normalized to boolean values and limited to map
 */
export function normalizeUserCapabilities(
  rawCapabilities: GetUserCapabilitiesResult<Array<string>>,
  capabilityMap: CapabilityNameMap,
): UserCapabilities {
  return Object.keys(rawCapabilities).reduce((acc, key) => {
    const flag: unknown = (rawCapabilities as any)[key];

    if (typeof flag === 'boolean' && (capabilityMap as any)[key]) {
      (acc as any)[(capabilityMap as any)[key]] = flag;
    }

    return acc;
  }, {});
}

/**
 * Returns a map of capabilities without the `can` and `has` prefixes to the versions
 * with the `can` and `has` prefixes. (e.g, `{ enterpise: 'hasEnterprise' }`).
 * The API expects fields without the prefixes, but we use the prefixes in code,
 * so we have to do this unfortunate dance.
 *
 * @param capabilities - array of capabilities in camelcase with can/has prefixes
 * @returns map of snake_cased capabilities (key is snake_case backend variant, value is camelCased prefixed one)
 */
export function getCapabilityMap(capabilities: Array<Capability>): CapabilityNameMap {
  return capabilities.reduce((acc, it) => {
    const field: Definable<string> = it.match(/^(can|has)(.+)/)?.[2];
    const key: string = decamelize(field ? field[0].toLowerCase() + field.slice(1) : it);

    (acc as any)[key] = it;

    return acc;
  }, {});
}
